import { LinkSimple } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { getLinkPreview, RichLinkPreview } from "../../../services/linkPreviewServices";
import { extractFirstMessageLink, parseChannelMarkupToDisplayHtml } from "../../../utils/messageLinks";
import { LinkPreviewCard, MessageTextSection } from "./styles";

export interface MessageTextContentProps {
  content: string;
  showPreview?: boolean;
}

const richPreviewCache = new Map<string, RichLinkPreview | null>();

export const MessageTextContent = ({
  content,
  showPreview = true,
}: MessageTextContentProps ) => {
  const linkPreview = showPreview ? extractFirstMessageLink( content ) : null;
  const linkPreviewURL = linkPreview?.url ?? "";
  const [ richPreview, setRichPreview ] = useState<RichLinkPreview | null>( null );

  useEffect(() => {
    setRichPreview( null );
    if( linkPreviewURL === "" ) return;

    const cached = richPreviewCache.get( linkPreviewURL );
    if( cached !== undefined ) {
      setRichPreview( cached );
      return;
    }

    const controller = new AbortController();

    getLinkPreview( linkPreviewURL, controller.signal ).then(( response ) => {
      if( controller.signal.aborted ) return;

      const preview = response.data || null;
      richPreviewCache.set( linkPreviewURL, preview );
      setRichPreview( preview );
    });

    return () => controller.abort();
  }, [ linkPreviewURL ]);

  if( content === "" ) return null;

  const previewURL = richPreview?.url ?? linkPreview?.url;
  const previewHost = richPreview?.host ?? linkPreview?.host;
  const previewTitle = richPreview?.title || richPreview?.siteName || linkPreview?.host;
  const previewSubtitle = richPreview?.description || linkPreview?.displayUrl;
  const previewLabel = richPreview?.siteName || previewHost || "Link";

  return (
    <>
      <MessageTextSection
        dangerouslySetInnerHTML={{ __html: parseChannelMarkupToDisplayHtml( content )}}
      />

      {
        linkPreview && previewURL && previewHost && (
          <LinkPreviewCard
            href={ previewURL }
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              richPreview?.imageUrl
                ? <img src={ richPreview.imageUrl } alt="" />
                : <div className="link-preview-icon">
                    <LinkSimple
                      size={ 18 }
                      weight="bold"
                    />
                  </div>
            }

            <div className="link-preview-info">
              <span>{ previewLabel }</span>
              <strong>{ previewTitle }</strong>
              <small>{ previewSubtitle }</small>
            </div>
          </LinkPreviewCard>
        )
      }
    </>
  );
};
